import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import PageShell from "../../components/layout/PageShell";
import {
    getPlatformSettingsByPrefix,
    savePlatformSettings
} from "../../services/crmService";
import { getSetting, toSettingPayload } from "./settingsUtils";

import "../Setting.css";

export default function AdminPlatformSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        enableVersionedDeployments: true,
        keepLastVersions: 5,
        enableAssetCacheBusting: true,
        enableRollback: true,
        autoRedeployAfterCrmPublish: true,
        deploymentTimeoutSeconds: 120,

        auditShowsLeadCountsOnly: true,
        superAdminCanViewLeadDetails: false,
        logSupportModeAccess: true,

        enableTracking: true,
        minimumViewsBeforeWinner: 100,
        leadConversionWeight: 5,
        autoSelectWinner: false,
        allowChallengerRegeneration: true
    });

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);

                const deploymentSettings = await getPlatformSettingsByPrefix("deployment.");
                const privacySettings = await getPlatformSettingsByPrefix("privacy.");
                const variantSettings = await getPlatformSettingsByPrefix("variant.");
                const settings = [
                    ...(deploymentSettings || []),
                    ...(privacySettings || []),
                    ...(variantSettings || [])
                ];

                if (!mounted) return;

                setForm({
                    enableVersionedDeployments: getSetting(settings, "deployment.enableVersionedDeployments", true),
                    keepLastVersions: getSetting(settings, "deployment.keepLastVersions", 5),
                    enableAssetCacheBusting: getSetting(settings, "deployment.enableAssetCacheBusting", true),
                    enableRollback: getSetting(settings, "deployment.enableRollback", true),
                    autoRedeployAfterCrmPublish: getSetting(settings, "deployment.autoRedeployAfterCrmPublish", true),
                    deploymentTimeoutSeconds: getSetting(settings, "deployment.timeoutSeconds", 120),

                    auditShowsLeadCountsOnly: getSetting(settings, "privacy.auditShowsLeadCountsOnly", true),
                    superAdminCanViewLeadDetails: getSetting(settings, "privacy.superAdminCanViewLeadDetails", false),
                    logSupportModeAccess: getSetting(settings, "privacy.logSupportModeAccess", true),

                    enableTracking: getSetting(settings, "variant.enableTracking", true),
                    minimumViewsBeforeWinner: getSetting(settings, "variant.minimumViewsBeforeWinner", 100),
                    leadConversionWeight: getSetting(settings, "variant.leadConversionWeight", 5),
                    autoSelectWinner: getSetting(settings, "variant.autoSelectWinner", false),
                    allowChallengerRegeneration: getSetting(settings, "variant.allowChallengerRegeneration", true)
                });
            } catch (error) {
                console.error(error);
                toast.error("Failed to load platform settings.");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, []);

    const update = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            await savePlatformSettings([
                toSettingPayload("deployment.enableVersionedDeployments", Boolean(form.enableVersionedDeployments), "bool"),
                toSettingPayload("deployment.keepLastVersions", Number(form.keepLastVersions || 0), "number"),
                toSettingPayload("deployment.enableAssetCacheBusting", Boolean(form.enableAssetCacheBusting), "bool"),
                toSettingPayload("deployment.enableRollback", Boolean(form.enableRollback), "bool"),
                toSettingPayload("deployment.autoRedeployAfterCrmPublish", Boolean(form.autoRedeployAfterCrmPublish), "bool"),
                toSettingPayload("deployment.timeoutSeconds", Number(form.deploymentTimeoutSeconds || 0), "number"),

                toSettingPayload("privacy.auditShowsLeadCountsOnly", Boolean(form.auditShowsLeadCountsOnly), "bool"),
                toSettingPayload("privacy.superAdminCanViewLeadDetails", Boolean(form.superAdminCanViewLeadDetails), "bool"),
                toSettingPayload("privacy.logSupportModeAccess", Boolean(form.logSupportModeAccess), "bool"),

                toSettingPayload("variant.enableTracking", Boolean(form.enableTracking), "bool"),
                toSettingPayload("variant.minimumViewsBeforeWinner", Number(form.minimumViewsBeforeWinner || 0), "number"),
                toSettingPayload("variant.leadConversionWeight", Number(form.leadConversionWeight || 0), "number"),
                toSettingPayload("variant.autoSelectWinner", Boolean(form.autoSelectWinner), "bool"),
                toSettingPayload("variant.allowChallengerRegeneration", Boolean(form.allowChallengerRegeneration), "bool")
            ]);

            toast.success("Platform settings saved.");
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data || "Failed to save platform settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageShell title="Deployment & Privacy Settings">
                <div className="project-loading-card">Loading platform settings...</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Deployment & Privacy Settings">
            <div className="settings-shell">
                <section className="project-hero-card">
                    <div>
                        <span className="project-kicker">Platform Settings</span>
                        <h1>Deployment & Privacy</h1>
                        <p>
                            Manage versioning, rollback, generated site cache behavior,
                            platform audit, and privacy boundaries.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </section>

                <section className="settings-form-grid">
                    <div className="project-panel">
                        <span className="project-kicker">Deployment</span>
                        <h2>Versioning</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Keep Last Versions</span>
                                <input
                                    type="number"
                                    value={form.keepLastVersions}
                                    onChange={(e) => update("keepLastVersions", e.target.value)}
                                />
                            </label>

                            <label>
                                <span>Deployment Timeout Seconds</span>
                                <input
                                    type="number"
                                    value={form.deploymentTimeoutSeconds}
                                    onChange={(e) => update("deploymentTimeoutSeconds", e.target.value)}
                                />
                            </label>
                        </div>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.enableVersionedDeployments}
                                    onChange={(e) => update("enableVersionedDeployments", e.target.checked)}
                                />
                                <span>Enable versioned deployments</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.enableAssetCacheBusting}
                                    onChange={(e) => update("enableAssetCacheBusting", e.target.checked)}
                                />
                                <span>Enable asset cache busting</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.enableRollback}
                                    onChange={(e) => update("enableRollback", e.target.checked)}
                                />
                                <span>Enable rollback</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.autoRedeployAfterCrmPublish}
                                    onChange={(e) => update("autoRedeployAfterCrmPublish", e.target.checked)}
                                />
                                <span>Auto redeploy after CRM publish</span>
                            </label>
                        </div>
                    </div>

                    <div className="project-panel">
                        <span className="project-kicker">Privacy</span>
                        <h2>Audit Boundaries</h2>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.auditShowsLeadCountsOnly}
                                    onChange={(e) => update("auditShowsLeadCountsOnly", e.target.checked)}
                                />
                                <span>Audit shows lead counts only</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.superAdminCanViewLeadDetails}
                                    onChange={(e) => update("superAdminCanViewLeadDetails", e.target.checked)}
                                />
                                <span>Allow super admin lead details support mode</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.logSupportModeAccess}
                                    onChange={(e) => update("logSupportModeAccess", e.target.checked)}
                                />
                                <span>Log support-mode access</span>
                            </label>
                        </div>
                    </div>

                    <div className="project-panel">
                        <span className="project-kicker">A/B Intelligence</span>
                        <h2>Variant Rules</h2>

                        <div className="settings-field-grid">
                            <label>
                                <span>Minimum Views Before Winner</span>
                                <input
                                    type="number"
                                    value={form.minimumViewsBeforeWinner}
                                    onChange={(e) => update("minimumViewsBeforeWinner", e.target.value)}
                                />
                            </label>

                            <label>
                                <span>Lead Conversion Weight</span>
                                <input
                                    type="number"
                                    value={form.leadConversionWeight}
                                    onChange={(e) => update("leadConversionWeight", e.target.value)}
                                />
                            </label>
                        </div>

                        <div className="settings-toggle-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.enableTracking}
                                    onChange={(e) => update("enableTracking", e.target.checked)}
                                />
                                <span>Enable A/B tracking</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.autoSelectWinner}
                                    onChange={(e) => update("autoSelectWinner", e.target.checked)}
                                />
                                <span>Auto-select winner</span>
                            </label>

                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.allowChallengerRegeneration}
                                    onChange={(e) => update("allowChallengerRegeneration", e.target.checked)}
                                />
                                <span>Allow challenger regeneration</span>
                            </label>
                        </div>
                    </div>
                </section>
            </div>
        </PageShell>
    );
}